import { useState } from "react";
import type { CaseRecord } from "../../types";
import { formatCents } from "../../lib/costsMath";
import type {
  CostsByContext as CostsByContextData,
  ContextGroup,
} from "../../lib/costsByContext";
import { AiMark, VerifiedMark } from "./ProvenanceMark";

// Costs-by-context table (Nav Phase 2 triage dashboard). Pure presentation over
// a pre-built deterministic table — it renders numbers, never computes them.
// Per-type subtotals always show (each traceable to its contributing facts);
// the net position is gated until cash categories are confirmed. Reversible
// exclusions (subsumed / user "already counted") live in the adjacent inspector
// with undo; only the unfixable unmatched amounts are listed here.
export default function CostsByContext({
  table,
  recordById,
  onNavigateToRecord,
}: {
  table: CostsByContextData;
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const unmatched = table.excluded.filter(
    (e) => e.kind === "unverified" || e.kind === "no_parseable_value",
  );
  return (
    <div className="ctx-table">
      <h4 className="overview-subsection-title">Costs by context</h4>
      <ul className="ctx-groups">
        {table.groups.map((g) => (
          <GroupRow
            key={g.bucket}
            group={g}
            recordById={recordById}
            onNavigateToRecord={onNavigateToRecord}
          />
        ))}
      </ul>

      {table.net ? (
        <div className="ctx-net">
          <span>Net position (costs − recoveries)</span>
          <span className="num">
            {formatCents(table.net.netCents)} <VerifiedMark what="Computed net" />
          </span>
        </div>
      ) : (
        <div className="ctx-net-suppressed" role="status">
          <strong>Net position isn’t shown yet.</strong>{" "}
          {table.netReason ??
            "Confirm what each amount is to compute the net."}{" "}
          The per-type subtotals above are exact regardless.
        </div>
      )}

      {unmatched.length > 0 && (
        <details className="ledger-excluded">
          <summary>
            {unmatched.length} amount{unmatched.length === 1 ? "" : "s"} the app
            couldn’t match to your documents — never counted
          </summary>
          <ul>
            {unmatched.map((e) => (
              <li key={e.key}>
                <button
                  type="button"
                  className="fact-source-link"
                  onClick={() => onNavigateToRecord?.(e.fact.record_id)}
                >
                  {e.fact.amount ?? e.fact.summary}
                </button>{" "}
                <span className="overview-muted">
                  (
                  {e.kind === "unverified"
                    ? "couldn’t read the value"
                    : "no clear number"}
                  )
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function GroupRow({
  group,
  recordById,
  onNavigateToRecord,
}: {
  group: ContextGroup;
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const expandable = group.count > 0;
  return (
    <li className={`ctx-group${open ? " open" : ""}`}>
      <button
        type="button"
        className="ctx-group-head"
        aria-expanded={open}
        disabled={!expandable}
        onClick={() => expandable && setOpen((v) => !v)}
      >
        <span className="ctx-group-label">
          {group.label}
          {!group.inNet && (
            <span className="ctx-ref-tag" title="A ceiling or unit price — shown for reference, never added into the net position.">
              reference, not in net
            </span>
          )}
        </span>
        <span className="ctx-group-meta">
          {group.count} item{group.count === 1 ? "" : "s"}
          {group.unconfirmedCount > 0 && (
            <span className="ctx-unconfirmed" title="Some contributing lines are still the app’s best guess — confirm them in the worklist above.">
              {" "}
              · {group.unconfirmedCount} unconfirmed
            </span>
          )}
        </span>
        <span className="num ctx-group-total">{formatCents(group.cents)}</span>
        {expandable && (
          <span className="ctx-caret">{open ? "▴" : "▾"}</span>
        )}
      </button>
      {open && (
        <ul className="ctx-items">
          {group.items.map((i) => {
            const rec = recordById.get(i.fact.record_id);
            return (
              <li key={i.key} className="ctx-item">
                <button
                  type="button"
                  className="fact-source-link ctx-item-rec"
                  onClick={() => onNavigateToRecord?.(i.fact.record_id)}
                >
                  {rec?.subject?.trim() || "Record"}
                </button>
                <span className="ctx-item-sentence">{i.fact.summary}</span>
                <span className="num">
                  {i.fact.trust_level === "low" ? "≈ " : ""}
                  {formatCents(i.cents)}
                </span>
                {i.confirmed ? (
                  <VerifiedMark what="Confirmed amount" />
                ) : (
                  <AiMark what="Type is the app’s best guess" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
